import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from '@/lib/zod-config';
import { Trash2, Pencil, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import HelpTooltip from '@/components/ui/help-tooltip';
import { useNotification } from '@/components/notifications';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { Checkbox } from '@/components/ui/checkbox';

import { EvalModel } from '@/types/evals-types';
import type { Eval } from '@/types/evals-types';
import {
  EvalV2Type,
  GoalAchievementEvalConfig,
  OutputQualityEvalConfig,
  FactualityEvalConfig,
  ToolUseEvalConfig,
  QuantitativeCheckEvalConfig,
  OUTPUT_QUALITY_DIMENSIONS,
  OutputQualityDimension,
  QUANTITATIVE_CHECK_DIMENSIONS,
  ENABLED_QUANTITATIVE_CHECKS,
  QuantitativeCheckDimension,
} from '@/types/eval-v2-types';
import { deleteEvalV2 } from '@/services/evaluation/eval-v2';
import { modelTypes, EVAL_NAME_MAX_LENGTH } from './base-eval-config';
import { PageTitle } from '@/components/page-title';
import EvalDescriptionPanel from './eval-description-panel';
import { useEvalTypeMetadata } from '@/hooks/queries/evaluation/use-get-eval-types';

type DimensionalEvalType =
  | 'goal_achievement'
  | 'output_quality'
  | 'factuality'
  | 'tool_use'
  | 'quantitative_check';

const TITLES: Record<DimensionalEvalType, string> = {
  goal_achievement: 'Goal Achievement Evaluation',
  output_quality: 'Output Quality Evaluation',
  factuality: 'Factuality Evaluation',
  tool_use: 'Tool Use Evaluation',
  quantitative_check: 'Quantitative Check Evaluation',
};

const DEFAULT_DESCRIPTIONS: Record<DimensionalEvalType, string> = {
  goal_achievement:
    "Judges whether the output achieves the upstream node's goal.",
  output_quality: 'Scores the output across configurable quality dimensions.',
  factuality:
    "Extracts claims and verifies them against sources (or the LLM's knowledge).",
  tool_use: "Evaluates the upstream agent's tool usage quality and efficiency.",
  quantitative_check:
    'Catches numeric mistakes — bad arithmetic, mismatched mentions, out-of-range values, malformed dates and units. Mix of pure-Python checks plus AI-assisted extraction for text and an analysis sandbox for spreadsheets and presentations.',
};

const DEFAULT_NAMES: Record<DimensionalEvalType, string> = {
  goal_achievement: 'Goal Achievement',
  output_quality: 'Output Quality',
  factuality: 'Factuality',
  tool_use: 'Tool Use',
  quantitative_check: 'Quantitative Check',
};

// Treat the page-level placeholder as "no name set" so we still pre-fill the
// type-specific default.
const PLACEHOLDER_NAMES = new Set(['', 'New evaluation']);

interface DimensionalEvalConfigProps {
  evalType: DimensionalEvalType;
  evalData: Eval;
  onSave: (data: Partial<Eval>) => void;
  onDuplicate: () => void;
  onDelete?: (evalId: string) => void;
}

// --- form schema ---

const baseSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(
      EVAL_NAME_MAX_LENGTH,
      `Name must be ${EVAL_NAME_MAX_LENGTH} characters or less`,
    ),
  description: z.string().trim().min(1, 'Description is required'),
  judge_model: z.nativeEnum(EvalModel),
  pass_threshold: z.number().min(0).max(10),
});

const outputQualitySchema = baseSchema.extend({
  dimensions: z
    .array(z.enum(OUTPUT_QUALITY_DIMENSIONS))
    .min(1, 'Select at least one dimension'),
  source_documents: z.string().nullable().optional(),
});

const factualitySchema = baseSchema.extend({
  source_documents: z.string().nullable().optional(),
});

const quantitativeCheckSchema = baseSchema.extend({
  enabled_checks: z
    .array(z.enum(QUANTITATIVE_CHECK_DIMENSIONS))
    .min(1, 'Select at least one check'),
});

type BaseFormValues = z.infer<typeof baseSchema>;
type OutputQualityFormValues = z.infer<typeof outputQualitySchema>;
type FactualityFormValues = z.infer<typeof factualitySchema>;
type QuantitativeCheckFormValues = z.infer<typeof quantitativeCheckSchema>;

type FormValues =
  | BaseFormValues
  | OutputQualityFormValues
  | FactualityFormValues
  | QuantitativeCheckFormValues;

// --- helpers ---

function getDefaultValues(
  evalType: DimensionalEvalType,
  evalData: Eval,
): FormValues {
  const cfg =
    (evalData?.eval_config as unknown as Record<string, unknown>) ?? {};
  const isNew = !!evalData?.is_new;
  const incomingName = evalData?.name?.trim() ?? '';
  // quantitative_check defaults to a higher threshold (8.0) because
  // its checks are largely deterministic — when the math passes,
  // it really passes. LLM-judged evals (output_quality,
  // goal_achievement, etc.) default to 6.0, matching the eval-
  // micro-service rubric calibration where "professionally usable"
  // is the pass bar (~6+) rather than "professionally polished"
  // (~7+).
  const defaultThreshold = evalType === 'quantitative_check' ? 8.0 : 6.0;
  const base = {
    // Pre-fill the type's default name for new evals (treating the page-level
    // "New evaluation" placeholder as empty).
    name:
      isNew && PLACEHOLDER_NAMES.has(incomingName)
        ? DEFAULT_NAMES[evalType]
        : incomingName,
    description:
      evalData?.description || (isNew ? DEFAULT_DESCRIPTIONS[evalType] : ''),
    judge_model: evalData?.model || EvalModel.CLAUDE_SONNET_46,
    pass_threshold:
      typeof cfg.pass_threshold === 'number'
        ? cfg.pass_threshold
        : defaultThreshold,
  };

  if (evalType === 'output_quality') {
    // Legacy configs may reference retired dimensions; without this filter Zod's
    // enum validator rejects the form on submit and strands the user. The
    // backend drops the same values at runtime.
    const validDims = new Set<string>(OUTPUT_QUALITY_DIMENSIONS);
    const rawDims = Array.isArray(cfg.dimensions)
      ? (cfg.dimensions as unknown[]).filter(
          (d): d is OutputQualityDimension =>
            typeof d === 'string' && validDims.has(d),
        )
      : [];
    return {
      ...base,
      dimensions: rawDims.length > 0 ? rawDims : ['completeness', 'coherence'],
      source_documents:
        (cfg.source_documents as string | null | undefined) ?? '',
    };
  }
  if (evalType === 'factuality') {
    return {
      ...base,
      source_documents:
        (cfg.source_documents as string | null | undefined) ?? '',
    };
  }
  if (evalType === 'quantitative_check') {
    return {
      ...base,
      enabled_checks: cfg.enabled_checks
        ? (cfg.enabled_checks as QuantitativeCheckDimension[]).filter((c) =>
            (ENABLED_QUANTITATIVE_CHECKS as readonly string[]).includes(c),
          )
        : [...ENABLED_QUANTITATIVE_CHECKS],
    };
  }
  return base;
}

function buildEvalConfig(
  evalType: DimensionalEvalType,
  data: FormValues,
): unknown {
  switch (evalType) {
    case 'goal_achievement':
      return {
        pass_threshold: data.pass_threshold,
      } satisfies GoalAchievementEvalConfig;
    case 'output_quality': {
      const d = data as OutputQualityFormValues;
      return {
        dimensions: d.dimensions,
        pass_threshold: d.pass_threshold,
        ...(d.source_documents?.trim()
          ? { source_documents: d.source_documents.trim() }
          : {}),
      } satisfies OutputQualityEvalConfig;
    }
    case 'factuality': {
      const d = data as FactualityFormValues;
      return {
        pass_threshold: d.pass_threshold,
        ...(d.source_documents?.trim()
          ? { source_documents: d.source_documents.trim() }
          : {}),
      } satisfies FactualityEvalConfig;
    }
    case 'tool_use':
      return {
        pass_threshold: data.pass_threshold,
      } satisfies ToolUseEvalConfig;
    case 'quantitative_check': {
      const d = data as QuantitativeCheckFormValues;
      return {
        enabled_checks: d.enabled_checks,
        pass_threshold: d.pass_threshold,
      } satisfies QuantitativeCheckEvalConfig;
    }
  }
}

function getSchema(evalType: DimensionalEvalType) {
  if (evalType === 'output_quality') return outputQualitySchema;
  if (evalType === 'factuality') return factualitySchema;
  if (evalType === 'quantitative_check') return quantitativeCheckSchema;
  return baseSchema;
}

// --- component ---

export default function DimensionalEvalConfig({
  evalType,
  evalData,
  onSave,
  onDuplicate,
  onDelete,
}: DimensionalEvalConfigProps) {
  const notification = useNotification();
  const metadata = useEvalTypeMetadata(evalType);
  const isExisting = !evalData.is_new;
  const [isEditing, setIsEditing] = useState(!isExisting);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: getDefaultValues(evalType, evalData) as FormValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(getSchema(evalType) as any),
  });

  useEffect(() => {
    form.reset(getDefaultValues(evalType, evalData) as FormValues);
    setIsEditing(!!evalData.is_new);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalData, evalType]);

  const handleSubmit = async (data: FormValues) => {
    const eval_config = buildEvalConfig(evalType, data);
    const partial: Partial<Eval> = {
      id: evalData.id,
      name: data.name,
      description: data.description,
      type: evalType,
      model: data.judge_model as EvalModel,
      eval_config: eval_config as Eval['eval_config'],
    };
    onSave(partial);
  };

  const handleDelete = async () => {
    if (!evalData.id) return;
    setIsDeleting(true);
    try {
      await deleteEvalV2(evalData.id);
      notification.success('Evaluation deleted');
      onDelete?.(evalData.id);
    } catch (e) {
      notification.error('Failed to delete evaluation');
      console.error('Delete eval error:', e);
      Sentry.captureException(e, {
        extra: {
          context: 'dimensional-eval-delete',
          evalId: evalData.id,
          evalType,
        },
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Form {...form}>
      <div
        className={`flex items-center justify-between ${metadata?.description ? 'mb-1' : 'mb-3'}`}
      >
        <PageTitle title={TITLES[evalType]} />
        {isExisting && !isEditing && (
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={onDuplicate}
            >
              <Copy className='h-4 w-4' />
              Duplicate
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setIsEditing(true)}
            >
              <Pencil className='h-4 w-4' />
              Edit
            </Button>
          </div>
        )}
      </div>
      {metadata?.description && (
        <p className='text-sm text-muted-foreground mb-3'>
          {metadata.description}
        </p>
      )}

      {metadata && <EvalDescriptionPanel metadata={metadata} />}

      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
        <div className='space-y-4'>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='label-required'>
                  Evaluation Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={DEFAULT_NAMES[evalType]}
                    maxLength={EVAL_NAME_MAX_LENGTH}
                    disabled={!isEditing}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) {
                        field.onChange(DEFAULT_NAMES[evalType]);
                      }
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='description'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='label-required'>Description</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={DEFAULT_DESCRIPTIONS[evalType]}
                    disabled={!isEditing}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) {
                        field.onChange(DEFAULT_DESCRIPTIONS[evalType]);
                      }
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='judge_model'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='label-required'>Judge Model</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!isEditing}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select a judge model' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {modelTypes.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='pass_threshold'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Pass Threshold
                  </FormLabel>
                  <HelpTooltip label='Pass threshold help'>
                    The minimum overall score (0-10) to count as PASS. The
                    evaluator returns scores per dimension and an overall
                    weighted score; if it meets or exceeds this threshold, the
                    eval verdict is PASS.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    type='number'
                    min={0}
                    max={10}
                    step={0.1}
                    disabled={!isEditing}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {evalType === 'output_quality' && (
            <Controller
              control={form.control}
              name={'dimensions' as const}
              render={({ field, fieldState }) => {
                const value = (field.value as OutputQualityDimension[]) || [];
                const toggle = (dim: OutputQualityDimension) => {
                  const next = value.includes(dim)
                    ? value.filter((d) => d !== dim)
                    : [...value, dim];
                  field.onChange(next);
                };
                return (
                  <FormItem>
                    <div className='flex items-center gap-2'>
                      <FormLabel className='label-required'>
                        Quality Dimensions
                      </FormLabel>
                      <HelpTooltip label='Dimensions help'>
                        Pick the dimensions to score. The judge returns a 0-10
                        score per dimension plus an evenly-weighted overall.
                      </HelpTooltip>
                    </div>
                    <div className='grid grid-cols-2 gap-2'>
                      {OUTPUT_QUALITY_DIMENSIONS.map((dim) => (
                        <label
                          key={dim}
                          className={`flex items-center gap-2 text-sm ${isEditing ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        >
                          <Checkbox
                            checked={value.includes(dim)}
                            onCheckedChange={() => isEditing && toggle(dim)}
                            disabled={!isEditing}
                          />
                          {dim.replace(/_/g, ' ')}
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className='text-sm text-destructive'>
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                );
              }}
            />
          )}

          {(evalType === 'output_quality' || evalType === 'factuality') && (
            <Controller
              control={form.control}
              name={'source_documents' as const}
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center gap-2'>
                    <FormLabel>Source Text (Optional)</FormLabel>
                    <HelpTooltip label='Source documents help'>
                      {evalType === 'factuality'
                        ? 'Reference text the evaluator should verify claims against. If omitted, the eval runs in "consistency mode" — checking for internal contradictions and against the LLM\'s knowledge, not authoritative sources.'
                        : 'Reference text the evaluator can use to assess groundedness and factual accuracy.'}
                    </HelpTooltip>
                  </div>
                  <FormControl>
                    <Textarea
                      placeholder='Paste reference text the evaluator should consult'
                      className='min-h-[120px]'
                      disabled={!isEditing}
                      {...field}
                      value={(field.value as string | null | undefined) ?? ''}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {evalType === 'goal_achievement' && (
            <p className='text-sm text-muted-foreground'>
              At runtime, this eval reads the upstream node&apos;s system prompt
              and task automatically. No additional config required.
            </p>
          )}

          {evalType === 'tool_use' && (
            <p className='text-sm text-muted-foreground'>
              At runtime, this eval reads tool call history from the upstream
              node&apos;s metadata. If no tool calls are present the eval
              returns &quot;no tool calls to evaluate&quot;.
            </p>
          )}

          {evalType === 'quantitative_check' && (
            <Controller
              control={form.control}
              name={'enabled_checks' as const}
              render={({ field, fieldState }) => {
                const value =
                  (field.value as QuantitativeCheckDimension[]) || [];
                const toggle = (check: QuantitativeCheckDimension) => {
                  const next = value.includes(check)
                    ? value.filter((d) => d !== check)
                    : [...value, check];
                  field.onChange(next);
                };
                return (
                  <FormItem>
                    <div className='flex items-center gap-2'>
                      <FormLabel className='label-required'>
                        Enabled Checks
                      </FormLabel>
                      <HelpTooltip label='Enabled checks help'>
                        Pick which deterministic checks to run. Each enabled
                        check is one dimension in the result. All checks run
                        with zero LLM cost in under 100ms.
                      </HelpTooltip>
                    </div>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                      {ENABLED_QUANTITATIVE_CHECKS.map((check) => (
                        <label
                          key={check}
                          className={`flex items-center gap-2 text-sm ${isEditing ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        >
                          <Checkbox
                            checked={value.includes(check)}
                            onCheckedChange={() => isEditing && toggle(check)}
                            disabled={!isEditing}
                          />
                          {check.replace(/_/g, ' ')}
                        </label>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className='text-sm text-destructive'>
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                );
              }}
            />
          )}
        </div>

        {isEditing && (
          <div className='flex justify-between items-center'>
            <div className='flex gap-2'>
              <Button type='submit'>{isExisting ? 'Save' : 'Create'}</Button>
              {isExisting && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    form.reset(
                      getDefaultValues(evalType, evalData) as FormValues,
                    );
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
            {onDelete && isExisting && (
              <Button
                type='button'
                variant='ghost'
                className='text-muted-foreground hover:text-red-600'
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <Trash2 className='h-4 w-4' />
                Delete
              </Button>
            )}
          </div>
        )}
      </form>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        title='Delete Evaluation'
        description='Are you sure you want to delete this evaluation? This action cannot be undone.'
        buttonLabel='Delete Evaluation'
        handleDelete={handleDelete}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </Form>
  );
}

// Convenience: small wrappers so the page can render a tab per type without
// passing evalType as a prop. These are thin shells around DimensionalEvalConfig.
export function GoalAchievementTab(
  props: Omit<DimensionalEvalConfigProps, 'evalType'>,
) {
  return <DimensionalEvalConfig {...props} evalType='goal_achievement' />;
}

export function OutputQualityTab(
  props: Omit<DimensionalEvalConfigProps, 'evalType'>,
) {
  return <DimensionalEvalConfig {...props} evalType='output_quality' />;
}

export function FactualityTab(
  props: Omit<DimensionalEvalConfigProps, 'evalType'>,
) {
  return <DimensionalEvalConfig {...props} evalType='factuality' />;
}

export function ToolUseTab(
  props: Omit<DimensionalEvalConfigProps, 'evalType'>,
) {
  return <DimensionalEvalConfig {...props} evalType='tool_use' />;
}

export function QuantitativeCheckTab(
  props: Omit<DimensionalEvalConfigProps, 'evalType'>,
) {
  return <DimensionalEvalConfig {...props} evalType='quantitative_check' />;
}

export type { DimensionalEvalType };
// Helper function for consumers that need to know the type union
export const isDimensionalEvalType = (
  type: string,
): type is DimensionalEvalType =>
  [
    'goal_achievement',
    'output_quality',
    'factuality',
    'tool_use',
    'quantitative_check',
  ].includes(type);

// Re-export to make the EvalV2Type set available without circular deps
export const TAB_KEY_FOR_TYPE: Record<EvalV2Type, string> = {
  pass_fail: 'pass_fail',
  score: 'score',
  exemplar: 'reference_output',
  goal_achievement: 'goal_achievement',
  output_quality: 'output_quality',
  factuality: 'factuality',
  tool_use: 'tool_use',
  quantitative_check: 'quantitative_check',
};
